//================================================================
//  Page: Application
//================================================================

//  Purpose: This page shows the current Application

//  Example:
//    <Application></Application>    

//================================================================


// Libraries
import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';

// Contexts
import { GetRoles, GetUser } from '../../../../Library/GlobalContexts';

// Components
import PageComponent from '../../../../Components/PageComponent/PageComponent';
import TabView from '../../../../Components/TabView/TabView';
import BreadCrumbs from '../../../../Components/Breadcrumbs/BreadCrumbs';
import StatusLabel from '../../../../Components/StatusLabel/StatusLabel';

// Tabs
import Overview from './Tabs/Overview';
import Workflows from './Tabs/Workflows';
import TechnicalArtefacts from './Tabs/TechnicalArtefacts';
import Allocation from './Tabs/Allocation';
import RiskAndCompliance from './Tabs/RiskAndCompliance';
import Support from './Tabs/Support';
import SupportingDocumentation from './Tabs/SupportingDocumentation';

// Functions
import DocumentListener from '../../../../Library/DocumentListener';
import CheckInventoryRole from '../../../../Library/CheckInventoryRole';

// Images

// CSS


export default function Application() {

  //------------------------------------------------------
  //  React Router & useContexts
  //------------------------------------------------------

  const routerUseLocation = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const getUser = useContext(GetUser);
  const getRoles = useContext(GetRoles);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState();

  // Holds the application document from 
  const [applicationDoc, setApplicationDoc] = useState();

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all Overview with the status of 'Active'
  useEffect(() => {

    const applicationId = params.id;

    if (applicationId === '') return;
    if (applicationId === null) return;
    if (getRoles === undefined) return;
    if (getUser === undefined) return;

    // ============================================
    //  Perform role check
    // ============================================

    const roleStatus = CheckInventoryRole('applications', getRoles, getUser);
    if (roleStatus === 'access-denied') return navigate('/inventory?view=applications');

    // ============================================
    //  Fetch Data
    // ============================================

    function onLoadChange(document) {

      if (document === undefined) return  setPageStatus('forbidden');

      setApplicationDoc(document);
      setPageStatus('onload')

    }

    function onError(error) {

      console.log(error);
      setPageStatus('error-fatal');

    }

    const unsubscribe = DocumentListener('applications', applicationId, onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser, getRoles]);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      pageStatus={pageStatus}
      header={
        <div className='Page-Header-Container' hidden={pageStatus !== 'onload'}>

          <BreadCrumbs
            rootLinkName='Applications'
            rootLinkRoute={`/${routerUseLocation.pathname.split('/')[1]}?view=applications`}
            childLinkName={applicationDoc?.applicationname}
          ></BreadCrumbs>

          <div className='flex flex-row gap-4'>
            <p className='font-medium text-[20px] mb-0'>{applicationDoc?.applicationname}</p>
            <StatusLabel status={applicationDoc?.applicationstatus}></StatusLabel>
          </div>
        </div>
      }
      body={
        <div>

          <TabView
            defaultView={1}

            //------------------------------------------------------
            // Overview
            //------------------------------------------------------

            oneTitle={'Overview'}
            oneRoute={`/inventory/application/${params.id}?view=overview`}
            oneContent={
              <Overview></Overview>
            }

            //------------------------------------------------------
            // Technical Artefacts
            //------------------------------------------------------

            twoTitle={'Technical Artefacts'}
            twoRoute={`/inventory/application/${params.id}?view=technicalartefacts`}
            twoContent={
              <TechnicalArtefacts></TechnicalArtefacts>
            }

            //------------------------------------------------------
            // Allocation
            //------------------------------------------------------

            threeTitle={'Cost Allocation'}
            threeRoute={`/inventory/application/${params.id}?view=costallocation`}
            threeContent={
              <Allocation></Allocation>
            }

            //------------------------------------------------------
            // Workflows
            //------------------------------------------------------

            fourTitle={'Workflows'}
            fourRoute={`/inventory/application/${params.id}?view=workflows`}
            fourContent={
              <Workflows></Workflows>
            }

            //------------------------------------------------------
            // Risk & Compliance
            //------------------------------------------------------

            fiveTitle={'Risk & Compliance'}
            fiveRoute={`/inventory/application/${params.id}?view=riskandcompliance`}
            fiveContent={
              <RiskAndCompliance></RiskAndCompliance>
            }

            //------------------------------------------------------
            // Support 
            //------------------------------------------------------

            sixTitle={'Support'}
            sixRoute={`/inventory/application/${params.id}?view=support`}
            sixContent={
              <Support></Support>
            }

            //------------------------------------------------------
            // Supporting Documentation 
            //------------------------------------------------------

            sevenTitle={'Supporting Documentation'}
            sevenRoute={`/inventory/application/${params.id}?view=documentation`}
            sevenContent={
              <SupportingDocumentation></SupportingDocumentation>
            }

          ></TabView>

        </div>
      }
    ></PageComponent>
  )
}
